/*
 * @Copyright 2025. Institute for Future Intelligence, Inc.
 */

import { getApp, getApps, initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { FirebaseName } from './constants';
import { getStorage } from 'firebase/storage';

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const apps = getApps();
export const app =
  apps.length === 0 || (apps.length === 1 && apps[0].name === FirebaseName.LOG_DATA) ? initializeApp(config) : getApp();

// App Check is opt-in: only initialized if the reCAPTCHA v3 site key is set
// in env. To enable end-to-end:
//   1. In Firebase Console → App Check, register the web app with reCAPTCHA v3
//      and copy the site key to VITE_FIREBASE_APPCHECK_SITE_KEY in .env.
//   2. Set APP_CHECK_ENFORCE=true on the deployed callable functions
//      (`firebase functions:config:set app_check.enforce=true` or via env)
//      so the V2 endpoints reject calls without a valid token.
// Both are done, so enforcement is LIVE. The App Check registration is keyed to
// the APP ID, not the project: if VITE_FIREBASE_APP_ID drifts from the app
// listed by `firebase apps:sdkconfig WEB`, exchangeRecaptchaV3Token starts
// returning 400, no token is ever minted, and every callable answers 401
// "Unauthenticated" while auth/Firestore/Storage keep working. After touching
// this config, load the site and check that the request to
// content-firebaseappcheck.googleapis.com/.../exchangeRecaptchaV3Token is 200.
const appCheckSiteKey = import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY;
if (appCheckSiteKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(appCheckSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

export const auth = getAuth(app);
export const firestore = getFirestore(app);
export const storage = getStorage(app);
